<!-- 
	@component
	Utility component to have a content component toggleable relative to another 
	"root" component.

	Usage:
	```svelte
	<Popover yPos="top" xPos="center">
		<IconButton slot="root"><QuestionMark /></IconButton> 
		<div>
			Here is some information about XYZ.
		</div>
	</Popover>
	```

 -->
<script lang="ts">
	import classNames from 'classnames';
	import { blur } from 'svelte/transition';
	import { quadInOut } from 'svelte/easing';

	export let isOpen = false;
	export let yPos: 'top' | 'bottom' = 'top';
	export let xPos: 'left' | 'center' | 'right' = 'left';

	let rootRef: Node | null = null;
	let contentRef: Node | null = null;

	// parent could force open/close with the isOpen but sometimes we just want to ask it to close not force it.
	export const requestClose = () => {
		isOpen = false;
	};

	function bindRoot(element: Node) {
		rootRef = element.firstChild;
	}

	function bindContent(element: Node) {
		contentRef = element.firstChild;
	}

	function open(e: MouseEvent | KeyboardEvent) {
		const { target } = e;
		if (target instanceof Node && rootRef?.contains(target)) {
			isOpen = true;
		}
	}

	function handleClose(e: MouseEvent) {
		const { target } = e;

		// close assuming the target wasn't one of the components related to
		// the popover
		if (
			target instanceof Node &&
			!rootRef?.contains(target) &&
			!contentRef?.contains(target)
		) {
			isOpen = false;
		}
	}

	function getPositionClasses() {
		const classes = [];
		if (xPos === 'left') {
			classes.push('left-0');
		} else if (xPos === 'center') {
			classes.push(' -translate-x-1/2 left-1/2');
		} else if (xPos === 'right') {
			classes.push('right-0');
		}

		if (yPos === 'top') {
			classes.push('bottom-full');
		} else if (yPos === 'bottom') {
			classes.push('top-full');
		}

		return classes;
	}
</script>

<svelte:window on:click={handleClose} />

<div
	use:bindRoot
	class="relative inline-block"
	on:click={open}
	on:click
	on:keypress={open}
>
	<slot name="root" />
	{#if isOpen}
		<div
			transition:blur={{ duration: 175, easing: quadInOut }}
			use:bindContent
			class={classNames('absolute z-30', getPositionClasses())}
		>
			<slot />
		</div>
	{/if}
</div>
